import AsyncDataWorker from "./DataWorker.ts?worker";
import { DataField } from "../channel/DynamicDataParser.ts";
import { getVersioning } from "../utils/Versioning.ts";

/* ---------------------------------------------------------------- *\
 *  Environment
\* ---------------------------------------------------------------- */
const isBrowser = typeof window !== "undefined";
const getNavigator = () => isBrowser ? navigator : null;

/* ---------------------------------------------------------------- *\
 *  Data worker state
\* ---------------------------------------------------------------- */
const dataWorkers = new Map<number, Worker>();
type PendingEntry = { payload: any, weight: number, enqueuedAt: number };
const dataPending = new Map<string, PendingEntry>();
const dataTaskResolvers = new Map<string, { resolve: Function, reject: Function }>();
const dataUpdateResolvers = new Map<number, { resolve: Function, reject: Function }>();
const dataUpdateResolves = new Map<number, number>();
let dataTaskSequence = 0;

// Gate subscriptions while updates are active
let activeDataUpdates = new Set<number>();
let waitingSubscriptions: Array<{
    resolve: (value: any) => void,
    reject: (reason: any) => void,
    id: string,
    payload: any,
    cause?: string
}> = [];

// Flush control
let dataInitialized = false;
let dataInitializing = false;
let dataInitPromise: Promise<void> | null = null;
let dataCurrentVersion = 0;
let dataFlushScheduled = false;

function getTaskWeight(payload: any): number {
    const metaWeight = Number(payload?.meta?.weight);
    const directWeight = Number(payload?.weight);
    if (Number.isFinite(metaWeight) && metaWeight > 0) return metaWeight;
    if (Number.isFinite(directWeight) && directWeight > 0) return directWeight;
    return 1;
}

function scheduleDataFlush() {
    if (dataFlushScheduled) return;
    dataFlushScheduled = true;
    Promise.resolve().then(() => {
        dataFlushScheduled = false;
        if (activeDataUpdates.size > 0) return;
        drainDataTasks();
    });
}



function rand(min: number, max: number): number {
    return Math.floor(Math.random() * (max - min + 1)) + min;
}

/* see docs/EXTRACTED_COMMENTS.md#c263 */
function failAllOutstanding(reason: string) {
    const error = new Error(reason);
    for (const resolver of dataTaskResolvers.values()) {
        resolver.reject(error);
    }
    dataTaskResolvers.clear();
    dataPending.clear();
    for (const resolver of dataUpdateResolvers.values()) {
        resolver.reject(error);
    }
    dataUpdateResolvers.clear();
    dataUpdateResolves.clear();
    activeDataUpdates.clear();
    const gated = waitingSubscriptions;
    waitingSubscriptions = [];
    for (const entry of gated) {
        entry.reject(error);
    }
}

function checkWaitingSubscriptions() {
    if (activeDataUpdates.size > 0) return;
    if (waitingSubscriptions.length > 0) {
        const toProcess = waitingSubscriptions;
        waitingSubscriptions = [];
        for (const entry of toProcess) {
            const { resolve, reject, id, payload } = entry;
            const taskId = `${id}#${++dataTaskSequence}`;
            // see docs/EXTRACTED_COMMENTS.md#c802
            if (payload && typeof payload === "object") payload.itemID = taskId;
            dataPending.set(taskId, {
                payload,
                weight: 1,
                enqueuedAt: performance.now()
            });
            dataTaskResolvers.set(taskId, { resolve, reject });
        }
    }
    if (dataPending.size > 0) {
        scheduleDataFlush();
    }
}


export async function initializeDataWorkers(): Promise<void> {
    if (!isBrowser) return;
    if (dataInitialized) return;
    if (dataInitializing) return dataInitPromise!;

    dataInitializing = true;
    dataInitPromise = (async () => {
        const nav = getNavigator();
        const hwc = nav?.hardwareConcurrency ?? 2;
        const envMax = Number((import.meta as any).env?.VITE_MAX_DATA_WORKERS ?? NaN);
        const maxDataWorkers = Number.isFinite(envMax) && envMax > 0 ? envMax : Math.max(2, hwc);

        const readyPromises: Promise<void>[] = [];

        for (let i = 0; i < maxDataWorkers; i++) {
            const worker = new AsyncDataWorker();

            readyPromises.push(
                new Promise<void>((resolve, reject) => {
                    const onMessage = (e: MessageEvent<any>) => {
                        if (e.data?.type === "INIT_DONE") {
                            worker.removeEventListener("message", onMessage);
                            resolve();
                        }
                    };
                    worker.addEventListener("message", onMessage);
                    worker.addEventListener("error", reject);
                    worker.postMessage({ messageType: "INIT" });
                })
            );

            worker.onmessage = (e: MessageEvent<any>) => {
                const msg = e.data;
                switch (msg.type) {
                    case "DATA_BATCH": {
                        for (const it of msg.items as Array<{ itemID: string; response: any }>) {
                            handleDataUpdate(it);
                        }
                        break;
                    }
                    case "DATA_PROCESSED": {
                        const id = msg.data;
                        const amount = dataUpdateResolves.get(id) || 0;
                        dataUpdateResolves.set(id, amount + 1);
                        if (dataUpdateResolves.get(id)! >= dataWorkers.size && dataUpdateResolvers.has(id)) {
                            dataUpdateResolvers.get(id)!.resolve(true);
                            dataUpdateResolvers.delete(id);
                            dataUpdateResolves.delete(id);
                        }
                        break;
                    }
                }
            };

            worker.addEventListener("error", (event) => {
                console.error(`[DataWorkerManager] worker ${i} error:`, event);
                failAllOutstanding(`data worker ${i} error: ${(event as ErrorEvent)?.message ?? "unknown"}`);
            });
            worker.addEventListener("messageerror", (event) => {
                console.error(`[DataWorkerManager] worker ${i} messageerror:`, event);
                failAllOutstanding(`data worker ${i} messageerror`);
            });

            dataWorkers.set(i, worker);
        }

        await Promise.all(readyPromises);
        dataInitialized = true;
        dataInitializing = false;
    })();

    await dataInitPromise;
}

/* see docs/EXTRACTED_COMMENTS.md#c620 */
export async function postClearAll(): Promise<void> {
    await initializeDataWorkers();
    let version = rand(0, 1e7);
    while (dataUpdateResolvers.has(version)) {
        version = rand(0, 1e7);
    }

    // see docs/EXTRACTED_COMMENTS.md#c621
    for (const [id, resolver] of dataTaskResolvers.entries()) {
        resolver.reject(new Error("postClearAll: session reset"));
        dataTaskResolvers.delete(id);
    }
    dataPending.clear();
    // see docs/EXTRACTED_COMMENTS.md#c622
    const gated = waitingSubscriptions;
    waitingSubscriptions = [];
    for (const entry of gated) {
        entry.reject(new Error("postClearAll: session reset"));
    }
    activeDataUpdates.clear();

    dataWorkers.forEach((worker) => {
        worker.postMessage({ id: version, messageType: "DATA_CLEAR_ALL" });
    });

    await new Promise<void>((resolve, reject) => {
        dataUpdateResolvers.set(version, {
            resolve: () => {
                resolve();
            },
            reject,
        });
    });
}

export async function postUpdate(action: { channel: number, row: number, data: DataField[] | null }, type: any, version: number = rand(0, 1e7)): Promise<boolean> {
    await initializeDataWorkers();

    while (dataUpdateResolvers.has(version)) {
        version = rand(0, 1e7);
    }

    activeDataUpdates.add(version);

    // Row data is plain strings; nothing to transfer.
    const payload = { id: version, action, messageType: type };
    dataWorkers.forEach((worker) => {
        worker.postMessage(payload);
    });

    return new Promise((resolve, reject) => {
        dataUpdateResolvers.set(version, {
            resolve: (result: any) => {
                activeDataUpdates.delete(version);
                try { getVersioning().markDataUpdated(); } catch (_e) {}
                checkWaitingSubscriptions();
                resolve(result);
            }, reject
        });
    });
}

/* see docs/EXTRACTED_COMMENTS.md#c623 */
export async function flushAndAwaitQueryable(): Promise<void> {
    await initializeDataWorkers();
    const version = rand(0, 1e7);
    dataWorkers.forEach((worker) => {
        worker.postMessage({ id: version, messageType: "DATA_FLUSH_ACK" });
    });
    await new Promise<void>((resolve, reject) => {
        dataUpdateResolvers.set(version, { resolve: () => resolve(), reject });
    });
}

export async function postTask(id: string, payload: any, cause: string = "unspecified"): Promise<any> {
    await initializeDataWorkers();

    if (activeDataUpdates.size > 0) {
        return new Promise((resolve, reject) => {
            waitingSubscriptions.push({ resolve, reject, id, payload, cause });
        });
    }
    return executeSubscriptionImmediate(id, payload);
}

async function executeSubscriptionImmediate(id: string, payload: any): Promise<any> {
    const taskId = `${id}#${++dataTaskSequence}`;
    // see docs/EXTRACTED_COMMENTS.md#c802
    if (payload && typeof payload === "object") payload.itemID = taskId;
    dataPending.set(taskId, {
        payload,
        weight: getTaskWeight(payload),
        enqueuedAt: (typeof performance !== "undefined" ? performance.now() : Date.now())
    });

    return new Promise((resolve, reject) => {
        dataTaskResolvers.set(taskId, { resolve, reject });
        scheduleDataFlush();
    });
}

function drainDataTasks() {
    if (dataPending.size === 0) return;

    const tasks = Array.from(dataPending.entries());
    dataPending.clear();

    const workerCount = dataWorkers.size;
    if (workerCount === 0) return;

    // see docs/EXTRACTED_COMMENTS.md#c624
    const buckets: Array<Array<[string, PendingEntry]>> = Array.from({ length: workerCount }, () => []);
    for (let i = 0; i < tasks.length; i++) {
        buckets[i % workerCount].push(tasks[i]);
    }

    const t0 = performance.now();
    let posted = 0;
    for (let wi = 0; wi < workerCount; wi++) {
        const bucket = buckets[wi];
        if (bucket.length === 0) continue;
        const worker = dataWorkers.get(wi);
        if (!worker) {
            for (const [id] of bucket) {
                dataTaskResolvers.get(id)?.reject("No data worker available");
                dataTaskResolvers.delete(id);
            }
            continue;
        }

        const bulkTasks = bucket.map(([, entry]) => {
            const version = (() => { try { return getVersioning().nextDataVersion(); } catch (_e) { return dataCurrentVersion++; } })();
            // see docs/EXTRACTED_COMMENTS.md#c802
            entry.payload._version = version;
            return entry.payload;
        });

        worker.postMessage({ messageType: "DATA_SUBSCRIPTION_BULK", tasks: bulkTasks });
        posted += bucket.length;
    }
    console.log(`[DataWorkerManager] drain: ${(performance.now() - t0).toFixed(1)}ms, posted ${posted}/${tasks.length} tasks across ${workerCount} workers`);
}

export function handleDataUpdate(responseData: any) {
    const { itemID, response } = responseData;
    const resolver = dataTaskResolvers.get(itemID);
    if (resolver) {
        resolver.resolve(response);
        dataTaskResolvers.delete(itemID);
        dataPending.delete(itemID);
    }
}

